import { call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import deviceService from 'src/services/deviceService';
import firebaseService, { getNotificationPermission, isWebView } from 'src/services/firebase';
import { ActionTypes } from 'src/store/constants';
import { message } from 'antd';
import { isDevelopment } from 'src/module/helpers';

// Action untuk initialize device
export function* initializeDevice() {
    const startedAt = Date.now();
    const traceId = `DEVICE-${startedAt}`;

    const log = (stage, details = {}) => {
        if (!isDevelopment) return;

        console.log(
            `[DEVICE_SAGA][${traceId}][${stage}]`,
            {
                timestamp: new Date().toISOString(),
                elapsedMs: Date.now() - startedAt,
                ...details,
            }
        );
    };

    const logError = (stage, error, details = {}) => {
        const normalizedError = {
            name: error?.name,
            message: error?.message || String(error),
            stack: error?.stack,
        };

        if (isDevelopment) {
            console.error(
                `[DEVICE_SAGA][${traceId}][${stage}]`,
                {
                    timestamp: new Date().toISOString(),
                    elapsedMs: Date.now() - startedAt,
                    ...details,
                    error: normalizedError,
                }
            );
        }
    };

    log("START", {
        event: "initializeDevice started",
    });

    try {
        // ==================================================
        // 1. READ REDUX STATE
        // ==================================================
        const state = yield select();

        const user = state?.authentication;
        const profile = state?.Profile?.response?.full;

        const isAuthenticated = Boolean(
            user?.isAuthenticated
        );

        const userId = profile?.id ?? null;

        log("STATE_LOADED", {
            isAuthenticated,
            hasAuthenticationState: Boolean(user),
            hasProfile: Boolean(profile),
            userId,
            hasDeviceStatus: Boolean(user?.deviceStatus),
            hasFirebaseState: Boolean(
                user?.deviceStatus?.firebaseState
            ),
        });

        // ==================================================
        // 2. CHECK PUSH PERMISSION
        // ==================================================
        let isAllowedPush = false;
        let pushSource = "unknown";
        let permission = "unknown";
        let hasAndroidToken = false;

        try {
            if (isWebView) {
                pushSource = "android_webview";

                const androidToken =
                    localStorage.getItem(
                        "fcm_token_android"
                    );

                hasAndroidToken = Boolean(androidToken);
                isAllowedPush = hasAndroidToken;
            } else {
                pushSource = "browser";

                permission =
                    getNotificationPermission();

                isAllowedPush =
                    permission === "granted";
            }
        } catch (permissionError) {
            logError(
                "PERMISSION_CHECK_FAILED",
                permissionError,
                {
                    pushSource,
                }
            );

            throw permissionError;
        }

        log("PUSH_PERMISSION_CHECKED", {
            pushSource,
            permission,
            isAllowedPush,
            hasAndroidToken,
        });

        if (!isAllowedPush) {
            log("ABORT_PUSH_DISABLED", {
                reason: isWebView
                    ? "Android FCM token unavailable"
                    : "Browser notification permission not granted",
                permission,
                pushSource,
            });

            yield put({
                type: ActionTypes.DEVICE_INIT_FAILURE,
                payload: {
                    message:
                        "Push notification disabled by settings",
                },
            });

            if (isDevelopment) {
                message.info(
                    "Push notification dinonaktifkan"
                );
            }

            return;
        }

        // ==================================================
        // 3. CHECK AUTHENTICATION
        // ==================================================
        if (!isAuthenticated) {
            log("ABORT_NOT_AUTHENTICATED", {
                reason: "User is not authenticated",
            });

            yield put({
                type: ActionTypes.DEVICE_INIT_FAILURE,
                payload: {
                    message: "User not logged in",
                },
            });

            return;
        }

        // ==================================================
        // 4. CHECK PROFILE
        // ==================================================
        if (!userId) {
            log("ABORT_PROFILE_MISSING", {
                reason: "Profile ID unavailable",
                hasProfile: Boolean(profile),
            });

            yield put({
                type: ActionTypes.DEVICE_INIT_FAILURE,
                payload: {
                    message:
                        "Profile ID unavailable",
                },
            });

            return;
        }

        log("PREPARE_DEVICE_INIT", {
            userId,
            pushSource,
        });

        // ==================================================
        // 5. INITIALIZE DEVICE
        // ==================================================
        log("DEVICE_INIT_REQUEST", {
            service: "deviceService.initializeDevice",
            userId,
        });

        const result = yield call(
            [
                deviceService,
                deviceService.initializeDevice,
            ],
            userId
        );

        log("DEVICE_INIT_RESPONSE", {
            success: Boolean(result?.success),
            message: result?.message,
            hasError: Boolean(result?.error),
            errorMessage:
                result?.error?.message ??
                (typeof result?.error === "string"
                    ? result.error
                    : undefined),
            resultExists: Boolean(result),
        });

        // ==================================================
        // 6. HANDLE SUCCESS
        // ==================================================
        if (result?.success) {
            const deviceStatus =
                deviceService.getStatus();

            log("DEVICE_INIT_SUCCESS", {
                userId,
                deviceStatus: {
                    isLoggedIn:
                        deviceStatus?.isLoggedIn,
                    hasToken: Boolean(
                        deviceStatus?.firebaseState?.token
                    ),
                },
            });

            yield put({
                type: ActionTypes.DEVICE_INIT_SUCCESS,
                payload: {
                    deviceStatus,
                    message: result?.message,
                },
            });

            // Setup foreground listener
            log("FOREGROUND_LISTENER_SETUP_START");

            yield call([
                firebaseService,
                firebaseService.setupForegroundListener,
            ]);

            log("FOREGROUND_LISTENER_SETUP_SUCCESS");

            if (isDevelopment) {
                message.success(
                    "Device berhasil diinisialisasi"
                );
            }

            log("COMPLETE", {
                success: true,
                userId,
                totalElapsedMs:
                    Date.now() - startedAt,
            });
        } else {
            log("DEVICE_INIT_FAILURE", {
                userId,
                message: result?.message,
                error: result?.error,
            });

            yield put({
                type: ActionTypes.DEVICE_INIT_FAILURE,
                payload: {
                    error: result?.error,
                    message:
                        result?.message ??
                        "Device initialization failed",
                },
            });

            if (isDevelopment) {
                message.warning(
                    `Device init gagal: ${result?.message ??
                    "Unknown error"
                    }`
                );
            }

            log("COMPLETE", {
                success: false,
                userId,
                totalElapsedMs:
                    Date.now() - startedAt,
            });
        }
    } catch (error) {
        logError("INITIALIZE_DEVICE_EXCEPTION", error);

        yield put({
            type: ActionTypes.DEVICE_INIT_FAILURE,
            payload: {
                error: error?.message || String(error),
                message: "Failed to initialize device",
            },
        });

        if (isDevelopment) {
            message.error(
                "Gagal menginisialisasi device"
            );
        }
    }
}

// Action untuk logout device
export function* logoutDevice() {
    try {
        console.log('[DEVICE_SAGA] Logging out device...');
        const state = yield select();
        const appSetting = state?.app_setting?.response
        const authentication = state?.authentication

        if (!Boolean(appSetting?.user_config?.push_notifications)) {
            yield put({
                type: ActionTypes.DEVICE_INIT_FAILURE,
                payload: {
                    message: "Push notification disabled by settings"
                }
            });

            if (isDevelopment)
                message.info("Push notification dinonaktifkan");

            return;
        }

        const result = yield call([deviceService, deviceService.logoutFromDevice], authentication);

        if (result?.success) {
            yield put({
                type: ActionTypes.DEVICE_LOGOUT_SUCCESS,
                payload: {
                    deviceStatus: deviceService.getStatus(),
                    message: result.message
                }
            });

            message.success('Berhasil logout dari device');

        } else {
            yield put({
                type: ActionTypes.DEVICE_LOGOUT_FAILURE,
                payload: {
                    error: result.error,
                    message: result.message
                }
            });

            message.warning(`Logout device gagal: ${result.message}`);
        }

    } catch (error) {
        console.error('[DEVICE_SAGA] Logout device error:', error);

        yield put({
            type: ActionTypes.DEVICE_LOGOUT_FAILURE,
            payload: {
                error: error.message,
                message: 'Failed to logout from device'
            }
        });

        message.error('Gagal logout dari device');
    }
}

// Action untuk get device status
export function* getDeviceStatus() {
    try {
        const status = yield call([deviceService, deviceService.getStatus]);

        yield put({
            type: ActionTypes.DEVICE_STATUS_SUCCESS,
            payload: { deviceStatus: status }
        });

    } catch (error) {
        console.error('[DEVICE_SAGA] Get device status error:', error);

        yield put({
            type: ActionTypes.DEVICE_STATUS_FAILURE,
            payload: {
                error: error.message,
                message: 'Failed to get device status'
            }
        });
    }
}

// Action untuk request notification permission
export function* requestNotificationPermission() {
    try {
        console.log('[DEVICE_SAGA] Requesting notification permission...');

        const permission = yield call([firebaseService, firebaseService.requestPermission]);

        yield put({
            type: ActionTypes.NOTIFICATION_PERMISSION_UPDATED,
            payload: { permission }
        });

        if (permission === 'granted') {
            // Initialize device jika permission granted
            yield call(initializeDevice);
            message.success('Izin notifikasi diberikan');
        } else if (permission === 'denied') {
            message.warning('Izin notifikasi ditolak');
        }

    } catch (error) {
        console.error('[DEVICE_SAGA] Request permission error:', error);

        yield put({
            type: ActionTypes.NOTIFICATION_PERMISSION_ERROR,
            payload: { error: error.message }
        });

        message.error('Gagal meminta izin notifikasi');
    }
}



// Watcher saga
export default function* deviceSaga() {
    yield takeLatest(ActionTypes.DEVICE_INIT_REQUEST, initializeDevice);
    yield takeLatest(ActionTypes.DEVICE_LOGOUT_REQUEST, logoutDevice);
    yield takeLatest(ActionTypes.DEVICE_STATUS_REQUEST, getDeviceStatus);
    yield takeLatest(ActionTypes.REQUEST_NOTIFICATION_PERMISSION, requestNotificationPermission);

    // Auto initialize device setelah login success
    // yield takeEvery(ActionTypes.USER_LOGIN_SUCCESS, function* (action) {
    //     if (!action.payload.requires2FA) {
    //         // Delay sedikit untuk memberikan waktu redirect
    //         yield call(delay, 1000);
    //         yield call(initializeDevice);
    //     }
    // });
}

